import {
  SectionDto,
  QuestionDto,
  QuestionTextDto,
  QuestionBoolDto,
  QuestionMultipleChoiceDto,
  QuestionSubSectionListDto,
  QuestionNumberDto,
  QuestionResponseBoolDto,
  QuestionResponseDateDto,
  QuestionResponseDto,
  QuestionResponseFileDto,
  QuestionResponseMultipleChoiceDto,
  QuestionResponseNumberDto,
  QuestionResponseTextDto,
  QuestionResponseTextListDto,
  SubSectionDto,
  SubSectionResponseDto,
  QuestionTabularChoiceDto,
  QuestionResponseTabularChoiceDto,
  OptionDto
} from 'models/dtoTypes';
import { get } from 'lodash';
import { date2str } from './date';
import { EWidget } from 'enums';
import { FileValue, ValueType } from 'typings/question';

export const getSubSection = (section: SectionDto, subSectionAlias: string): SubSectionDto | undefined => {
  const { subSections } = section;
  if (Array.isArray(subSections)) {
    const sub = subSections.find(s => s.alias === subSectionAlias);
    return sub;

  } else {
    console.error('There is not subSections for this section.');
    return undefined;
  }
};

export const getSubSectionResponses = (section: SectionDto, subSectionAlias: string): SubSectionResponseDto[] => {
  const subSectionResponses = get(section, ['sectionResponse', 'subSectionResponses']) as SubSectionResponseDto[];
  if (Array.isArray(subSectionResponses)) {
    return subSectionResponses.filter(s => s.subSectionAlias === subSectionAlias);
  } else {
    console.error('subSectionResponses is not an array');
    return [] as SubSectionResponseDto[];
  }
};

export const generateSingleResponse = (questionAlias: string, questionType: string, formValue: ValueType): QuestionResponseDto => {
  switch (questionType) {
    case 'QuestionBoolDto': {
      return {
        questionAlias,
        type: 'QuestionResponseBoolDto',
        value: formValue as boolean
      } as QuestionResponseBoolDto;
    }
    case 'QuestionFileListDto': {
      const { file, data } = (formValue as FileValue);
      const { name } = file;
      const extension = name.split('.').pop() || '';
      const fileInfo = {
        fileName: name,
        data: data.split(',').pop(),
        extension
      };
      return {
        questionAlias,
        type: 'QuestionResponseFileDto',
        ...fileInfo
      } as QuestionResponseFileDto;
    }
    case 'QuestionMultipleChoiceDto': {
    // multiple choices vs single choice, both should be converted to array of optionAliases
      const selectedOptions = Array.isArray(formValue) ? (formValue as OptionDto[]) : [formValue as OptionDto];
      const optionAliases = selectedOptions.map(o => o.alias);
      return {
        questionAlias,
        type: 'QuestionResponseMultipleChoiceDto',
        optionAliases,
        selectedOptions,
      } as QuestionResponseMultipleChoiceDto;
    }
    case 'QuestionTabularChoiceDto': {
      const selectedOptions = Array.isArray(formValue) ? (formValue as OptionDto[]) : [formValue as OptionDto];
      return {
        questionAlias,
        type: 'QuestionResponseTabularChoiceDto',
        selectedOptions,
      } as QuestionResponseTabularChoiceDto;
    }
    case 'QuestionNumberDto': {
      return {
        questionAlias,
        type: 'QuestionResponseNumberDto',
        value: (formValue || undefined) as number
      } as QuestionResponseNumberDto;
    }
    case 'QuestionTextDto': {
      return {
        questionAlias,
        type: 'QuestionResponseTextDto',
        value: formValue as string
      } as QuestionResponseTextDto;
    }
    case 'QuestionTextListDto': {
      return {
        questionAlias,
        type: 'QuestionResponseTextListDto',
        values: formValue as string[]
      } as QuestionResponseTextListDto;
    }
    case 'QuestionDateDto': {
      return {
        questionAlias,
        type: 'QuestionResponseDateDto',
        value: date2str(formValue as moment.Moment)
      } as QuestionResponseDateDto;
    }
    default:
      return {
        questionAlias,
        type: 'QuestionResponseDto'
      } as QuestionResponseDto;
  }
};

const getNumberWidget = (format?: string): EWidget => {
  switch (format) {
    case 'Range': return EWidget.RANGE;
    case 'Currency': return EWidget.CURRENCY;
    case 'Percentage': return EWidget.PERCENTAGE;
    default: return EWidget.NUMBER;
  }
};

const getSelectWidget = (format: string, allowMultiple: boolean): EWidget => {
  // "CheckboxList" | "Select" | "RadioList";
  switch (format) {
    case 'AutoComplete': return EWidget.SINGLE_AUTOCOMPLETE;
    case 'CheckboxList': return EWidget.CHECKBOXES;
    case 'RadioList': return EWidget.RADIO;
    case 'HsCode': return EWidget.HS;
    case 'EsgTaxonomy': return EWidget.ESG_TAXONOMY;
    case 'ClimateMitigationActivity': return EWidget.CLIMATE_MIGRATION_ACTIVITY;
    case 'ClimateAdaptationActivity': return EWidget.CLIMATE_ADAPTATION_ACTIVITY;
    default: return allowMultiple ? EWidget.MULTIPLE : EWidget.SINGLE_SELECT;
  }
};

const getTabularWidget = (format: string, allowMultiple: boolean): EWidget => {
  switch (format) {
    case 'CheckboxList': return EWidget.TABULAR_MULTIPLE;
    case 'RadioList': return EWidget.TABULAR_SINGLE;
    default: return allowMultiple ? EWidget.TABULAR_MULTIPLE : EWidget.TABULAR_SINGLE;
  }
};

const getSubSectionListWidget = (format?: string): EWidget => {
  switch (format) {
    case 'DirectorOrShareholder': return EWidget.DIRECTOR_OR_SHAREHOLDER_INPUT;
    default: return EWidget.SUB_SECTION;
  }
};

export const getFieldWidget = (field: QuestionDto): EWidget => {
  const { type } = field || {};

  switch (type) {
    case 'QuestionTextDto': {
      const { format } = field as QuestionTextDto;
      return format === 'Phone' ? EWidget.PHONE : EWidget.TEXT;
    }
    case 'QuestionDateDto': return EWidget.DATE;
    case 'QuestionMultipleChoiceDto': {
      const { allowMultiple, format } = field as QuestionMultipleChoiceDto;
      return getSelectWidget(format || '', allowMultiple);
    }
    case 'QuestionTabularChoiceDto': {
      const { allowMultiple, format } = field as QuestionTabularChoiceDto;
      return getTabularWidget(format || '', allowMultiple);
    }
    case 'QuestionBoolDto': {
      const { format } = field as QuestionBoolDto;
      return format === 'Checkbox' ? EWidget.CHECKBOX : EWidget.BOOL_SELECT;
    }
    case 'QuestionNumberDto':
      return getNumberWidget((field as QuestionNumberDto).format);
    case 'QuestionFileListDto': return EWidget.FILES;
    case 'QuestionTextListDto': return EWidget.TEXT_LIST;
    case 'QuestionSubSectionListDto': {
      const { format } = field as QuestionSubSectionListDto;
      return getSubSectionListWidget(format);
    }
    // TODO: Returning type here is somehow unacceptable to ESLint
    default: return EWidget.UNKNOWN;
  }
};

// Nested form's files are submitted together with others
export const getSubSectionFieldWidget = (field: QuestionDto): EWidget => {
  const { type } = field || {};
  if (type === 'QuestionFileListDto') {
    return EWidget.IN_FORM_FILES;
  } else {
    return getFieldWidget(field);
  }
};

